import { Tooltip } from '@mui/material';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../components/utils';
import { splitTagsForDisplay } from './alternate-exercise-search';

const CHIP_CLASSNAME =
  'inline-flex items-center whitespace-nowrap rounded-sm border ' +
  'border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium ' +
  'leading-none text-gray-700';

// Gap (px) between chips, kept in sync with the `gap-1` (4px) row spacing so
// width measurement matches what the browser lays out.
const CHIP_GAP = 4;

type ExerciseTag = { tag: { name: string } };

/**
 * A single non-wrapping row of movement-tag chips for an exercise search
 * result. Trailing chips that do not fit collapse into a keyboard-focusable
 * `+N` chip whose accessible name (and tooltip, on hover and focus) lists the
 * hidden tags, so the information is never hover-only.
 *
 * Width is measured in the browser; `maxVisibleTags` overrides the measurement
 * (used by tests, where layout has no real width) and makes the split
 * deterministic.
 */
export function ExerciseTagChips({
  tags,
  maxVisibleTags,
}: {
  tags: ExerciseTag[];
  maxVisibleTags?: number;
}) {
  const { t } = useTranslation();
  const tagNames = useMemo(
    () => tags.map((t) => t.tag.name).filter((name): name is string => !!name),
    [tags],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const [measuredVisible, setMeasuredVisible] = useState(tagNames.length);
  // Track hover and focus independently so the overflow tooltip stays open while
  // either is active (never hover-only). A single flag would let onMouseLeave
  // close the tooltip while the chip is still focused.
  const [overflowHovered, setOverflowHovered] = useState(false);
  const [overflowFocused, setOverflowFocused] = useState(false);

  useLayoutEffect(() => {
    // Explicit override (tests / callers) skips measurement entirely.
    if (maxVisibleTags !== undefined) {
      return;
    }
    const container = containerRef.current;
    const ruler = rulerRef.current;
    if (
      !container ||
      !ruler ||
      typeof ResizeObserver === 'undefined' ||
      typeof getComputedStyle === 'undefined'
    ) {
      return;
    }

    const recompute = () => {
      const available = container.clientWidth;
      if (available <= 0) {
        return;
      }
      const chipEls = Array.from(
        ruler.querySelectorAll<HTMLElement>('[data-ruler-chip]'),
      );
      const overflowEl = ruler.querySelector<HTMLElement>(
        '[data-ruler-overflow]',
      );
      const overflowWidth = overflowEl
        ? overflowEl.getBoundingClientRect().width + CHIP_GAP
        : 0;

      let used = 0;
      let count = chipEls.length;
      for (let i = 0; i < chipEls.length; i++) {
        const chipEl = chipEls[i];
        if (!chipEl) {
          continue;
        }
        const chipWidth =
          chipEl.getBoundingClientRect().width + (i > 0 ? CHIP_GAP : 0);
        const isLast = i === chipEls.length - 1;
        // Reserve room for the "+N" chip unless this is the final chip and the
        // whole row fits.
        const needed = used + chipWidth + (isLast ? 0 : overflowWidth);
        if (needed > available) {
          count = i;
          break;
        }
        used += chipWidth;
      }
      setMeasuredVisible(count);
    };

    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    recompute();
    return () => observer.disconnect();
  }, [maxVisibleTags, tagNames]);

  if (tagNames.length === 0) {
    return null;
  }

  const effectiveMax = maxVisibleTags ?? measuredVisible;
  const { visible, hidden } = splitTagsForDisplay(tagNames, effectiveMax);
  const hiddenLabel = hidden.join(', ');

  return (
    <div className="relative min-w-0 overflow-hidden">
      {/* Visible, clipped row. */}
      <div
        ref={containerRef}
        className="flex min-w-0 items-center gap-1 overflow-hidden"
      >
        {visible.map((name, index) => (
          <span key={`${name}-${index}`} className={CHIP_CLASSNAME}>
            {name}
          </span>
        ))}
        {hidden.length > 0 && (
          <Tooltip
            title={hiddenLabel}
            open={overflowHovered || overflowFocused}
          >
            <button
              type="button"
              // Visible "+N" is included in the accessible name (WCAG 2.5.3)
              // ahead of the hidden tag names, so the label matches what's shown.
              aria-label={t('exerciseTagChipOverflowLabel', {
                hiddenCount: hidden.length,
                hiddenTags: hiddenLabel,
              })}
              // Same chip styling/height as the tag chips; shrink-0 keeps it from
              // being squeezed as the row overflows.
              className={cn(CHIP_CLASSNAME, 'shrink-0 cursor-default')}
              onFocus={() => setOverflowFocused(true)}
              onBlur={() => setOverflowFocused(false)}
              onMouseEnter={() => setOverflowHovered(true)}
              onMouseLeave={() => setOverflowHovered(false)}
            >
              +{hidden.length}
            </button>
          </Tooltip>
        )}
      </div>

      {/* Off-screen ruler: always holds every chip so widths stay stable while
          the visible row is being trimmed. Hidden from AT and pointer. */}
      <div
        ref={rulerRef}
        aria-hidden="true"
        className="pointer-events-none absolute -top-96 left-0 flex items-center gap-1"
        style={{ visibility: 'hidden' }}
      >
        {tagNames.map((name, index) => (
          <span
            key={`ruler-${name}-${index}`}
            data-ruler-chip
            className={CHIP_CLASSNAME}
          >
            {name}
          </span>
        ))}
        <span data-ruler-overflow className={CHIP_CLASSNAME}>
          +{tagNames.length}
        </span>
      </div>
    </div>
  );
}
